import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Calendar, ArrowLeft, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Article non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // État de chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <title>Chargement... | Blog Hilanta</title>
        <Loader2 className="animate-spin text-terracotta" size={40} />
      </div>
    );
  }

  // État d'erreur ou article non trouvé
  if (error || !post) {
    return (
      <div className="min-h-screen bg-cream py-16">
        <title>Article non trouvé | Blog Hilanta</title>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <BookOpen className="mx-auto text-warm-brown-light mb-4" size={48} />
          <h1 className="font-heading text-2xl text-warm-brown mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-terracotta hover:underline font-body">
            ← Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  // Construire le titre de manière sécurisée
  const pageTitle = (post.title || 'Article') + ' | Blog Hilanta';
  const pageDescription = post.excerpt || '';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-sand-light" data-testid="blog-post-page">
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      
      {/* Back link */}
      <div className="bg-sand py-3">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to="/blog"
            className="text-terracotta hover:text-terracotta-dark font-body text-sm flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 text-warm-brown-light text-sm mb-4">
              <Calendar size={16} />
              <span>{formatDate(post.created_at)}</span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl text-warm-brown mb-6">
              {post.title}
            </h1>
            
            {post.image_url && (
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-auto"
                />
              </div>
            )}
          </header>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8">
            <div 
              className="font-body text-warm-brown leading-relaxed prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-warm-brown
                prose-p:text-warm-brown prose-p:leading-relaxed
                prose-a:text-terracotta prose-a:no-underline hover:prose-a:underline
                prose-strong:text-warm-brown
                prose-ul:text-warm-brown prose-ol:text-warm-brown
                prose-li:text-warm-brown"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {post.content}
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-terracotta hover:text-terracotta-dark font-body"
            >
              <ArrowLeft size={16} />
              Voir tous les articles
            </Link>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
